import React, { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import QRCode from 'react-qr-code';
import { useDevice } from '../../../contexts/DeviceContext';
import { buildPlaylistActivationUrl } from '../../../utils/activationUrl';

interface ActivationSectionProps {
  focused: boolean;
}

const PENDING_POLL_INTERVAL_MS = 10_000;

const ActivationSection: React.FC<ActivationSectionProps> = ({ focused }) => {
  const { t } = useTranslation();
  const { displayCode, devicePassword, licenseStatus, playlistActivationUrl, refreshActivation } = useDevice();
  const refreshBtnRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (focused) {
      refreshBtnRef.current?.focus();
    }
  }, [focused]);

  // Auto-poll while pending — stops as soon as status changes or component unmounts
  const refreshActivationRef = useRef(refreshActivation);
  useEffect(() => { refreshActivationRef.current = refreshActivation; }, [refreshActivation]);

  useEffect(() => {
    if (licenseStatus !== 'pending_activation') return;
    void refreshActivationRef.current();
    const timer = setInterval(() => { void refreshActivationRef.current(); }, PENDING_POLL_INTERVAL_MS);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [licenseStatus]);

  const handleRefresh = useCallback(() => {
    void refreshActivation();
  }, [refreshActivation]);

  const qrUrl = displayCode
    ? buildPlaylistActivationUrl(playlistActivationUrl, displayCode, devicePassword)
    : '';

  const statusKey = (() => {
    switch (licenseStatus) {
      case 'active':              return 'settings.activation.statusActive';
      case 'pending_activation':  return 'settings.activation.statusPending';
      case 'expired':             return 'settings.activation.statusExpired';
      case 'revoked':             return 'settings.activation.statusRevoked';
      default:                    return 'settings.activation.statusBootstrapping';
    }
  })();

  const statusVariant = (() => {
    switch (licenseStatus) {
      case 'active':             return 'active';
      case 'pending_activation': return 'pending';
      case 'expired':            return 'expired';
      case 'revoked':            return 'revoked';
      default:                   return 'bootstrapping';
    }
  })();

  return (
    <div className="settings-section">
      <h2 className="settings-section__title">{t('settings.activation.title')}</h2>

      {/* Codes card */}
      <div className="settings-card">
        <div className="settings-row">
          <span className="settings-row__label">{t('settings.activation.deviceCode')}</span>
          <span className="settings-row__value settings-row__value--code">
            {displayCode || '…'}
          </span>
        </div>
        <div className="settings-row">
          <span className="settings-row__label">{t('settings.activation.password')}</span>
          <span className="settings-row__value settings-row__value--code">
            {devicePassword || '…'}
          </span>
        </div>
        <div className="settings-row">
          <span className="settings-row__label">{t('settings.activation.status')}</span>
          <span className={`settings-badge settings-badge--${statusVariant}`}>
            {t(statusKey)}
          </span>
        </div>
        <div>
          <button
            ref={refreshBtnRef}
            className="settings-btn"
            onClick={handleRefresh}
          >
            {t('settings.activation.refreshBtn')}
          </button>
        </div>
      </div>

      {/* QR code + tutorial card */}
      {qrUrl && (
        <div className="settings-card" style={{ alignItems: 'flex-start', gap: 16, flexDirection: 'row' }}>
          {/* QR */}
          <div className="settings-qr">
            <QRCode value={qrUrl} size={140} level="M" />
          </div>

          {/* Tutorial */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div className="settings-row__label" style={{ fontWeight: 600, marginBottom: 2 }}>
              {t('settings.activation.tutorialTitle')}
            </div>
            <div style={{ fontSize: 13, color: 'var(--color-text-secondary, #aaa)', lineHeight: 1.5 }}>
              <div style={{ marginBottom: 4 }}>
                <span style={{ color: 'var(--color-text-primary, #fff)', fontWeight: 600 }}>1.</span>{' '}
                {t('settings.activation.tutorialStep1')}{' '}
                <span style={{ color: '#60a5fa', fontFamily: 'monospace', wordBreak: 'break-all' }}>
                  {playlistActivationUrl}
                </span>
              </div>
              <div style={{ marginBottom: 4 }}>
                <span style={{ color: 'var(--color-text-primary, #fff)', fontWeight: 600 }}>2.</span>{' '}
                {t('settings.activation.tutorialStep2')}
              </div>
              <div>
                <span style={{ color: 'var(--color-text-primary, #fff)', fontWeight: 600 }}>3.</span>{' '}
                {t('settings.activation.tutorialStep3')}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ActivationSection;
